import { useState, useEffect } from 'react';
import { formatPhoneNumber } from '../utils/api';

export default function CallModal({ isOpen, onClose, contractor, onCallInitiated, mode = 'call' }) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [formattedNumber, setFormattedNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [callStatus, setCallStatus] = useState(null);
  const [callId, setCallId] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const isCallTrackingGloballyEnabled = process.env.NEXT_PUBLIC_CALL_TRACKING_ENABLED === 'true';
  const directPhoneNumber = isCallTrackingGloballyEnabled ? (contractor.callrail_tracking_number || contractor.phone) : contractor.phone;

  // Format phone number as user types
  useEffect(() => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    setFormattedNumber(formatPhoneNumber(cleaned));
  }, [phoneNumber]);

  // Handle countdown for automatic close
  useEffect(() => {
    let timer;
    if (callStatus === 'initiated' && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (callStatus === 'initiated' && countdown === 0) {
      onClose();
      setCallStatus(null);
      setCallId(null);
      setCountdown(5);
    }
    return () => clearTimeout(timer);
  }, [callStatus, countdown, onClose]);

  // Poll for call status if we have a callId
  useEffect(() => {
    let statusTimer;
    
    const checkCallStatus = async () => {
      if (!callId) return;
      
      try {
        const response = await fetch(`/api/call/status?callId=${callId}`);
        const data = await response.json();
        
        if (data.success) {
          if (data.status === 'connected') {
            // Call is connected, show success message
            setCallStatus('connected');
            if (onCallInitiated) onCallInitiated(callId);
          } else if (data.status === 'failed') {
            // Call failed
            setCallStatus('failed');
            setError('Call failed. Please try again or call directly.');
          } else if (data.status === 'completed' && data.duration > 0) {
            // Call completed successfully
            setCallStatus('completed');
            clearTimeout(statusTimer);
          }
        }
      } catch (error) {
        console.error('Error checking call status:', error);
      }
    };
    
    if (callId && callStatus === 'initiated') {
      // Check status immediately
      checkCallStatus();
      // Then poll every 3 seconds
      statusTimer = setInterval(checkCallStatus, 3000);
    }
    
    return () => clearInterval(statusTimer);
  }, [callId, callStatus, onCallInitiated]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate phone number
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    
    setIsSubmitting(true);
    
    // Direct call mode (CallRail integration)
    if (mode === 'call') {
      try {
        // Format phone number in E.164 format
        const e164Number = `+1${cleaned}`;
        
        // Call the API to initiate the call
        const response = await fetch('/api/call/initiate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contractorId: contractor.id,
            userPhone: e164Number
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          setCallStatus('initiated');
          setCallId(data.callId);
        } else {
          setError(data.message || 'Failed to initiate call');
        }
      } catch (error) {
        console.error('Error initiating call:', error);
        setError('Failed to connect. Please try again or call directly.');
      } finally {
        setIsSubmitting(false);
      }
    } 
    // Callback request mode (email notification)
    else if (mode === 'callback') {
      try {
        const response = await fetch('/api/call/request-callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contractorId: contractor.id,
            customerName: customerName,
            customerPhone: `+1${cleaned}`,
            customerEmail: customerEmail,
            preferredTime: preferredTime,
            message: message
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          setCallStatus('requested');
        } else {
          setError(data.message || 'Failed to send callback request');
        }
      } catch (error) {
        console.error('Error requesting callback:', error);
        setError('Failed to send request. Please try again or call directly.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  // Reset the form when modal is closed
  const handleClose = () => {
    setPhoneNumber('');
    setFormattedNumber('');
    setCustomerName('');
    setCustomerEmail('');
    setPreferredTime('');
    setMessage('');
    setError('');
    setCallStatus(null);
    setCallId(null);
    setCountdown(5);
    onClose();
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            {mode === 'call' ? 'Call' : 'Request Callback from'} {contractor.company_name}
          </h2>
          <button 
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {callStatus === 'initiated' ? (
          <div className="text-center py-4">
            <div className="animate-pulse mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Calling You Now</h3>
            <p className="text-gray-600 mb-4">
              Please answer your phone when it rings. We&apos;re connecting you with {contractor.company_name}.
            </p>
            <div className="text-sm text-gray-500">
              This window will close in {countdown} seconds...
            </div>
          </div>
        ) : callStatus === 'connected' || callStatus === 'completed' ? (
          <div className="text-center py-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Call Connected!</h3>
            <p className="text-gray-600 mb-4">
              You are now connected with {contractor.company_name}.
            </p>
            <button
              onClick={handleClose}
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            >
              Close
            </button>
          </div>
        ) : callStatus === 'requested' ? (
          <div className="text-center py-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Request Sent!</h3>
            <p className="text-gray-600 mb-4">
              Your callback request has been sent to {contractor.company_name}. They will contact you as soon as possible.
            </p>
            <button
              onClick={handleClose}
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            >
              Close
            </button>
          </div>
        ) : mode === 'call' ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <p className="text-gray-600 mb-4">
                Enter your phone number below and we&apos;ll call you right away to connect you with {contractor.company_name}. This is our direct connect service - we&apos;ll connect you without sharing your number.
              </p>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Your Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={formattedNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-lg py-2 px-3"
                required
              />
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>
            
            <div className="text-xs text-gray-500 mb-4">
              <p>By clicking &quot;Call Me Now&quot;, you acknowledge:</p>
              <ul className="list-disc list-inside mt-1">
                <li>Your number will only be used for this specific call</li>
                <li>Calls may be recorded for quality assurance</li>
                <li>Standard carrier rates may apply</li>
              </ul>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <button
                type="submit"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Connecting...' : 'Call Me Now'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500">
                Or call directly: <a href={`tel:${directPhoneNumber}`} className="text-blue-600 font-medium">{formatPhoneNumber(directPhoneNumber)}</a>
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <p className="text-gray-600 mb-4">
                Fill out this form to request a callback from {contractor.company_name}. They will contact you as soon as possible.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="customerName"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2 px-3"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    value={formattedNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2 px-3"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Email (Optional)
                  </label>
                  <input
                    type="email"
                    id="customerEmail"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="johndoe@example.com"
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2 px-3"
                  />
                </div>
                
                <div>
                  <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Callback Time (Optional)
                  </label>
                  <select
                    id="preferredTime"
                    value={preferredTime}
                    onChange={(e) => setPreferredTime(e.target.value)}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2 px-3"
                  >
                    <option value="">Any time</option>
                    <option value="morning">Morning (8am - 12pm)</option>
                    <option value="afternoon">Afternoon (12pm - 5pm)</option>
                    <option value="evening">Evening (5pm - 8pm)</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Briefly describe your water damage issue..."
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2 px-3"
                    rows={3}
                  />
                </div>
              </div>
              
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>
            
            <div className="text-xs text-gray-500 mb-4">
              <p>By clicking &quot;Request Callback&quot;, you acknowledge:</p>
              <ul className="list-disc list-inside mt-1">
                <li>Your information will be shared with the contractor</li>
                <li>The contractor may contact you via phone or email</li>
              </ul>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending Request...' : 'Request Callback'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500">
                Or call directly: <a href={`tel:${directPhoneNumber}`} className="text-blue-600 font-medium">{formatPhoneNumber(directPhoneNumber)}</a>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}